/**
 * Bob Laadt Op — bob-collection-grid (collectiepagina).
 *
 * 1. "Bekijk nu"-knopje volgt de muis over elke productkaart (--cx/--cy).
 * 2. Filteren: binnen één groep is de keuze een OF, tussen groepen een EN
 *    ("tot 5 kWh" OF "vanaf 8 kWh") EN ("tot € 1.500").
 *    Elke chip heeft data-min en optioneel data-max (bovengrens exclusief).
 * 3. Sorteren, wis-knoppen, lege staat en een live-telling voor screenreaders.
 * Bron: werkbestanden/paginas/thuisbatterijen.html r. 1615–1756.
 *
 * Reveal-on-scroll doet bob-base.js (.prod-grid > .prod-card); hier zorgen we
 * alleen dat een kaart die door een filter terugkomt niet op opacity 0 blijft.
 * Teksten komen uit Liquid (data-count-template), niets hardcoded.
 */
class BobCollectionGrid extends HTMLElement {
  connectedCallback() {
    this.abort = new AbortController();
    const { signal } = this.abort;

    this.grid = /** @type {HTMLElement|null} */ (this.querySelector('[data-grid]'));
    if (!this.grid) return;
    this.cards = /** @type {HTMLElement[]} */ (Array.from(this.grid.querySelectorAll('.prod-card')));
    this.bobOrder = this.cards.slice();
    this.chips = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('.filter-chip')));
    this.sortSelect = /** @type {HTMLSelectElement|null} */ (this.querySelector('[data-sort]'));
    this.countEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-count]'));
    this.resetBtn = /** @type {HTMLElement|null} */ (this.querySelector('.filter-reset'));
    this.emptyEl = /** @type {HTMLElement|null} */ (this.querySelector('[data-empty]'));

    /* ── Cursor-knop op de productkaarten volgt de muis ─────────────── */
    this.cards.forEach((card) => {
      const cursor = /** @type {HTMLElement|null} */ (card.querySelector('.prod-cursor'));
      if (!cursor) return;
      /** @param {PointerEvent} e */
      const place = (e) => {
        const r = card.getBoundingClientRect();
        cursor.style.setProperty('--cx', e.clientX - r.left + 'px');
        cursor.style.setProperty('--cy', e.clientY - r.top + 'px');
      };
      card.addEventListener(
        'pointerenter',
        (e) => {
          if (e.pointerType !== 'mouse') return;
          cursor.style.transition = 'none';
          place(e);
          void cursor.offsetWidth;
          cursor.style.transition = '';
        },
        { signal }
      );
      card.addEventListener(
        'pointermove',
        (e) => {
          if (e.pointerType !== 'mouse') return;
          place(e);
        },
        { signal }
      );
    });

    /* ── Chip-aantallen: hoeveel kaarten vallen in elk bereik ────────── */
    this.chips.forEach((chip) => {
      const countEl = chip.querySelector('.filter-chip-count');
      if (countEl) countEl.textContent = String(this.cards.filter((card) => this.inRange(chip, card)).length);
    });

    /* ── Filteren & sorteren ─────────────────────────────────────────── */
    this.chips.forEach((chip) => {
      chip.addEventListener(
        'click',
        () => {
          chip.setAttribute('aria-pressed', chip.getAttribute('aria-pressed') === 'true' ? 'false' : 'true');
          this.apply();
        },
        { signal }
      );
    });
    this.sortSelect?.addEventListener('change', () => this.apply(), { signal });
    this.querySelectorAll('[data-reset-filters]').forEach((btn) => {
      btn.addEventListener('click', () => this.reset(), { signal });
    });

    /* Theme editor: een geselecteerde kaart die weggefilterd is weer tonen. */
    this.addEventListener(
      'shopify:block:select',
      (e) => {
        const card = e.target instanceof Element ? e.target.closest('.prod-card') : null;
        if (card instanceof HTMLElement && card.hidden) this.reset();
      },
      { signal }
    );

    this.apply(true);
  }

  disconnectedCallback() {
    this.abort?.abort();
  }

  /**
   * Getal uit een data-attribuut; NaN als het ontbreekt.
   * @param {HTMLElement} card
   * @param {string} group - 'capaciteit' of 'prijs'
   */
  valueOf(card, group) {
    return parseFloat(group === 'prijs' ? card.dataset.prijs ?? '' : card.dataset.capaciteit ?? '');
  }

  /**
   * Valt de kaart in het bereik van de chip? Onder- t/m bovengrens, bovengrens exclusief.
   * @param {HTMLElement} chip
   * @param {HTMLElement} card
   */
  inRange(chip, card) {
    const value = this.valueOf(card, chip.dataset.filter || '');
    if (Number.isNaN(value)) return false;
    const min = parseFloat(chip.dataset.min ?? '');
    const max = parseFloat(chip.dataset.max ?? '');
    return value >= (Number.isNaN(min) ? 0 : min) && value < (Number.isNaN(max) ? Infinity : max);
  }

  /** @param {HTMLElement} card */
  matches(card) {
    /** @type {Record<string, HTMLElement[]>} */
    const active = {};
    this.chips.forEach((chip) => {
      if (chip.getAttribute('aria-pressed') !== 'true') return;
      const group = chip.dataset.filter || '';
      (active[group] ||= []).push(chip);
    });
    return Object.values(active).every((chips) => chips.some((chip) => this.inRange(chip, card)));
  }

  sort() {
    if (!this.grid) return;
    const mode = this.sortSelect?.value || 'bob';
    const order = this.bobOrder.slice();
    /**
     * Kaarten zonder getal achteraan.
     * @param {string} group
     * @param {number} dir
     */
    const by = (group, dir) => (/** @type {HTMLElement} */ a, /** @type {HTMLElement} */ b) => {
      const va = this.valueOf(a, group);
      const vb = this.valueOf(b, group);
      if (Number.isNaN(va)) return Number.isNaN(vb) ? 0 : 1;
      if (Number.isNaN(vb)) return -1;
      return (va - vb) * dir;
    };
    if (mode === 'prijs-op') order.sort(by('prijs', 1));
    else if (mode === 'prijs-af') order.sort(by('prijs', -1));
    else if (mode === 'cap-op') order.sort(by('capaciteit', 1));
    else if (mode === 'cap-af') order.sort(by('capaciteit', -1));
    order.forEach((card) => this.grid?.appendChild(card));
  }

  /** @param {boolean} [initial] - bij de eerste keer de reveal-animatie niet overslaan */
  apply(initial = false) {
    const activeCount = this.chips.filter((chip) => chip.getAttribute('aria-pressed') === 'true').length;
    let shown = 0;

    this.cards.forEach((card) => {
      const fits = this.matches(card);
      card.hidden = !fits;
      if (fits) {
        shown++;
        /* Een kaart die pas ná het scrollen weer tevoorschijn komt, moet niet
           op opacity 0 blijven staan van de reveal-animatie. */
        if (!initial) card.classList.add('is-visible');
      }
    });

    this.sort();

    if (this.countEl) {
      const template = this.countEl.dataset.countTemplate || '';
      this.countEl.textContent = template
        .replace('[shown]', String(shown))
        .replace('[total]', String(this.cards.length));
    }
    if (this.resetBtn) this.resetBtn.hidden = activeCount === 0;
    if (this.emptyEl) this.emptyEl.hidden = shown !== 0;
    if (this.grid) this.grid.hidden = shown === 0;
  }

  reset() {
    this.chips.forEach((chip) => chip.setAttribute('aria-pressed', 'false'));
    this.apply();
  }
}

if (!customElements.get('bob-collection-grid')) {
  customElements.define('bob-collection-grid', BobCollectionGrid);
}
